import { useState, useEffect, useRef } from 'react';
import { NetworkLegend } from './NetworkLegend';
import { useTranslation } from 'react-i18next';
import { SearchWithTranslation } from '@/components/Form/SearchWithTranslation';
import { graphql, useQuery } from '@/lib/graphql';
import { Core } from 'cytoscape';

import { TopicTagWithFilterTooltip } from '../tooltips/TopicTagWithFilterTooltip';
import { TagContainer } from '@/features/analyze/components/Tag';
import { CollapsibleTagList } from '@/components/Elements';
import { NetworkData, NodeType } from '@/features/connect-dots/types/cytoscape';
import { StopHighlightButton } from '../visualizer/StopHighlightButton';
import { useNodeSearch } from '@/features/connect-dots/hooks/useNodeSearch';

import { ClearSearchButton } from '@/features/connect-dots/components/visualizer/ClearSearchButton';
import { useFilterConditionAtom } from '@/features/analyze/atoms/filterCondition.atom';

type ToolbarProps = {
  cytoscapeCoreRefObject: Core | null;
  filteredData: NetworkData | null;
  nodeTypeFilters: { [key in NodeType]: boolean };
  updateNodeTypeFilter: (nodeType: NodeType, isVisible: boolean) => void;
};

const getTopicTagsQuery = graphql(`
  query GetTopicTags(
    $condition: TopicTagSearchConditionInput!
    $page: Int = 1
    $perPage: Int = 9999
  ) {
    topicTags(condition: $condition, page: $page, perPage: $perPage) {
      items {
        name
      }
      pageInfo {
        currentPage
        hasNextPage
        totalCount
      }
    }
  }
`);

export function Toolbar({
  cytoscapeCoreRefObject,
  filteredData,
  nodeTypeFilters,
  updateNodeTypeFilter,
}: ToolbarProps) {
  const { t } = useTranslation();
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(
    undefined
  );
  const [filterCondition, setFilterCondition] = useFilterConditionAtom();
  const [translatedText, setTranslatedText] = useState<string>(
    filterCondition.query ?? ''
  ); // 検索クエリを保持する状態

  const themeLabelOptions = (filteredData?.nodes ?? [])
    .filter((n) => n.nodeType === NodeType.THEME)
    .map((n) => {
      return (
        <option key={n.id} value={n.id}>
          {n.label ?? t('未設定')}
        </option>
      );
    })
    .filter(Boolean) as JSX.Element[];

  themeLabelOptions?.sort((a, b) => {
    const labelA = a.props.children.toLowerCase();
    const labelB = b.props.children.toLowerCase();
    return labelA.localeCompare(labelB);
  });

  const handleNodeFlushRef =
    useRef<(topic_tags_list: readonly string[]) => void>();

  const {
    searchedNodes,
    setSearchedNodes,
    isDropdownOpen,
    setIsDropdownOpen,
    highlightSearchResults,
    highlightNodeAndConnections,
    removeHighlighting,
    clearSearchResults,
  } = useNodeSearch();

  useEffect(() => {
    handleNodeFlushRef.current = (topic_tags_list: readonly string[]) => {
      if (!cytoscapeCoreRefObject) return;
      const matchedNodes = highlightSearchResults(
        cytoscapeCoreRefObject,
        topic_tags_list
      );
      setSearchedNodes(matchedNodes);
      setIsDropdownOpen(true);
    };
  }, [
    cytoscapeCoreRefObject,
    highlightSearchResults,
    setSearchedNodes,
    setIsDropdownOpen,
  ]);

  // GraphQL クエリを実行
  const { data, error } = useQuery(getTopicTagsQuery, {
    variables: {
      condition: { query: translatedText },
      page: 1,
      perPage: 9999,
    },
    skip: !translatedText, // translatedText が空の場合はクエリをスキップ
  });

  // クエリ結果の処理
  useEffect(() => {
    if (error) {
      console.error('Error fetching topic tags:', error);
      return;
    }

    if (data && handleNodeFlushRef.current) {
      handleNodeFlushRef.current(
        data.topicTags.items.map((item: { name: string }) => item.name)
      );
    }
  }, [data, error]);

  const clearInputValue = () => {
    setTranslatedText('');
    setFilterCondition((prev) => ({ ...prev, query: undefined }));
    clearSearchResults(cytoscapeCoreRefObject);
  };

  if (!cytoscapeCoreRefObject) {
    return null; // Cytoscapeが初期化されていない場合は何も表示しない
  }

  return (
    <>
      <div className="relative my-2 ml-2">
        <div className="flex justify-between gap-4">
          <div className="relative flex-1">
            {/* 検索バー */}
            <div className="mb-2  min-w-0">
              <SearchWithTranslation
                onSuccess={(translatedText: string) => {
                  setTranslatedText(translatedText);
                }}
              />
            </div>
            {/* タグリスト表示部分 */}
            {isDropdownOpen && (
              <div className="relative">
                <div
                  className="absolute left-0 top-full z-20 mt-2 max-h-64 overflow-y-auto rounded border bg-white px-4 pb-4 pt-2 shadow-lg"
                  style={{
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)', // ドロップダウンの影
                  }}
                >
                  <div className="flex justify-between pb-2 text-sm text-gray-600">
                    <span>{t('もしかして・・・')}</span>
                    {/* 検索結果のクリア */}
                    <div className="pointer-events-none z-10 ml-16 text-xs">
                      <div className="pointer-events-auto">
                        <ClearSearchButton
                          onClick={() => {
                            clearInputValue();
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {searchedNodes.length === 0 ? (
                    <div className="py-2 text-sm text-gray-500">
                      {t('該当するタグが見つかりません')}
                    </div>
                  ) : (
                    <TagContainer>
                      <CollapsibleTagList
                        items={searchedNodes ?? []}
                        maxVisibleItems={12}
                        textColor="text-rw-black"
                        renderTag={(item) => (
                          <TopicTagWithFilterTooltip
                            cytoscapeCoreRefObject={cytoscapeCoreRefObject}
                            content={item}
                          />
                        )}
                      />
                    </TagContainer>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0">
            {/* 表示設定メニュー と テーマ選択 */}
            <NetworkLegend
              nodeCount={filteredData?.nodes.length ?? 0}
              nodeTypeFilters={nodeTypeFilters}
              updateNodeTypeFilter={updateNodeTypeFilter}
            />

            <div className="relative mx-2 py-2">
              {/* StopHighlightButton - フローティング表示 */}
              <div className="pointer-events-none absolute right-48 top-1/2 z-10 -translate-y-1/2">
                <div className="pointer-events-auto">
                  <StopHighlightButton
                    cytoscapeCoreRefObject={cytoscapeCoreRefObject}
                  />
                </div>
              </div>

              <select
                className="rounded border px-2 py-1 text-sm"
                value={selectedThemeId}
                onChange={(e) => {
                  setSelectedThemeId(e.target.value);
                  if (cytoscapeCoreRefObject && e.target.value) {
                    const target = cytoscapeCoreRefObject.getElementById(
                      e.target.value
                    );
                    if (target && target.length > 0) {
                      cytoscapeCoreRefObject.animate({
                        center: { eles: target },
                        duration: 600,
                        easing: 'ease-in-out',
                        zoom: 0.8,
                      });
                      removeHighlighting(cytoscapeCoreRefObject);
                      highlightNodeAndConnections(
                        cytoscapeCoreRefObject,
                        target.id()
                      );
                    }
                  }
                }}
              >
                <option value="">{t('テーマへ移動...')}</option>
                {themeLabelOptions}
              </select>
            </div>
          </div>
        </div>
      </div>
      <hr></hr>
    </>
  );
}
